import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {connect} from 'react-redux';
import getContactNumberValidationFunctions from '../field-validation/getContactNumberValidationFunctions';
import InputField from '../../../form/InputField';
import InputFieldHelpLabel from '../../../form/InputFieldHelpLabel';
import Language from '../../../lang/language';
import Translation from '../../../lang/Translation';
import {getIsMobileNumber} from '../../../selectors/ContactNumberSelectors';
import {hasContactNumberHelpLabel} from '../../../selectors/DeliveryOptionsSelectors/DeliveryOptionsSelectors';
import getContactNumberMaxLength from '../../../selectors/ContactNumberValidationSelectors/getContactNumberMaxLength';
import getContactNumberMinLength from '../../../selectors/ContactNumberValidationSelectors/getContactNumberMinLength';
import getRegexValidation from '../../../selectors/ContactNumberValidationSelectors/getRegexValidation';
import getAutocompleteStatus from '../../../selectors/getAutocompleteStatus';
import {
  getContactNumber, getDeliveryType, getSettings, getSiteCode, selectIsContactNumberRequired,
} from '../../../selectors/selectors';
import {TooltipAndOverlayIDs} from '../../detailed-tooltip/TooltipLibrary';
import useStyles from './ContactNumber.styles';

export function ContactNumber(props) {
  const classes = useStyles();
  
  const fieldName = 'contactNumber';
  
  const helpLabel = props.requiresHelpLabel && (
    <InputFieldHelpLabel
      overlay={TooltipAndOverlayIDs.CONTACT_NUMBER_INFO}
      linkTranslationKey="checkout.delivery.address.field.contact.number.hint.link"
      fieldName={fieldName}
      leftAlign
    />
  );
  
  const collectionHelpLabel = (
    <div className={classes.collectionHelpLabel}>
      <Translation>checkout.delivery.address.field.collection.contact.number.help.text</Translation>
    </div>
  );
  
  const selectedHelpLabel = (props.newCollectionOverlay && props.findDeliveryType === 'pudo') ? collectionHelpLabel : helpLabel;
  
  const getMobileNumber = (key) => {
    if (Language.doesTranslationExist(`${key}.${props.siteCode}`)) {
      return `${key}.${props.siteCode}`;
    }
    
    return key;
  };
  
  useEffect(() => {
    if (props.shouldValidate) {
      props.fieldContainer.validate();
    }
  }, []);
  
  const definedField = {
    name: fieldName,
    placeholder: 'checkout.delivery.address.field.mobile.number.placeholder.text',
    validate: getContactNumberValidationFunctions(props),
    minLength: props.minLength,
    maxLength: props.maxLength,
    required: props.isRequired || props.isBillingContactNumber,
    label: props.isMobileNumber && !props.isBillingContactNumber
      ? getMobileNumber('checkout.delivery.address.field.mobile.number.label.text')
      : 'checkout.delivery.address.field.contact.number.label.text',
    helperText: !props.isBillingContactNumber && selectedHelpLabel,
    autoComplete: 'tel',
    type: 'tel',
    shouldNotCollapse: true,
  };

  return (
    <div
      data-test="ContactNumber"
      className={props.shouldShowContactNumberAfterAddressee ? classes.contactNumberExperiment : classes.contactNumber}
    >
      <InputField
        fieldContainer={props.fieldContainer}
        definedField={definedField}
        value={props.value}
        legacyNames={{wrappedClassName: 'contact-number', nameInClass: 'contact-number'}}
        autoCompleteState={props.autoCompleteState}
        fieldNamePrefix={props.isBillingContactNumber ? 'billingAddress' : 'deliveryAddress'}
      />
    </div>
  );
}

ContactNumber.propTypes = {
  fieldContainer: PropTypes.shape({
    deregisterField: PropTypes.func.isRequired,
    registerField: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
  }).isRequired,
  value: PropTypes.string,
  customClass: PropTypes.string,
  customId: PropTypes.string,
  customContainerId: PropTypes.string,
  isRequired: PropTypes.bool.isRequired,
  isMobileNumber: PropTypes.bool,
  requiresHelpLabel: PropTypes.bool,
  showOverlay: PropTypes.func.isRequired,
  minLength: PropTypes.number.isRequired,
  maxLength: PropTypes.number.isRequired,
  isBillingContactNumber: PropTypes.bool,
  contactNumberValue: PropTypes.string,
  autoCompleteState: PropTypes.string,
  regexValidation: PropTypes.string,
  siteCode: PropTypes.string,
  shouldShowContactNumberAfterAddressee: PropTypes.bool,
  newCollectionOverlay: PropTypes.bool,
  findDeliveryType: PropTypes.string,
  shouldValidate: PropTypes.bool,
};

ContactNumber.defaultProps = {
  isBillingContactNumber: false,
};

const mapStateToProps = (state, ownProps) => {
  const receivedValue = ownProps.isBillingContactNumber ? ownProps.contactNumberValue : getContactNumber(state);
  const newCollectionOverlay = getSettings(state)?.should_use_new_collection_overlay;
  const findDeliveryType = getDeliveryType(state);
  
  return {
    isMobileNumber: getIsMobileNumber(state),
    isRequired: selectIsContactNumberRequired(state),
    requiresHelpLabel: hasContactNumberHelpLabel(state),
    minLength: getContactNumberMinLength(state),
    maxLength: getContactNumberMaxLength(state),
    regexValidation: getRegexValidation(state),
    value: receivedValue || '',
    autoCompleteState: getAutocompleteStatus(state),
    siteCode: getSiteCode(state),
    newCollectionOverlay,
    findDeliveryType,
  };
};

export default connect(
  mapStateToProps,
)(ContactNumber);
